// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-19
// protected: NO
// =============================================================================
// Laporan Mutasi (read-only): saldo awal/masuk/keluar/akhir per lokasi & periode + breakdown.
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, BarChart3, Search, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { WAREHOUSES, STORES, downloadFile, allowedLocations } from "@/v2/components/helpers";
import { TableScroll } from "@/v2/components/TableScroll";

const Stat = ({ label, value, accent }) => (
  <Card className="p-4 rounded-2xl">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-xl font-semibold tabular-nums mt-1 ${accent || ""}`}>{value}</p>
  </Card>
);

export default function MutasiPage() {
  const { api, me } = useV2Auth();
  const ALL_LOC = allowedLocations(me);   // lokasi sesuai scope pengguna
  const [loc, setLoc] = useState(ALL_LOC[0] || WAREHOUSES[0]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (location) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ location: location || loc });
      if (from) qs.set("date_from", from);
      if (to) qs.set("date_to", to);
      const r = await api(`/api/v2/reports/mutasi?${qs.toString()}`);
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat laporan"); setData(null); return; }
      setData(d);
    } finally { setLoading(false); }
  }, [api, loc, from, to]);

  useEffect(() => { run(ALL_LOC[0] || WAREHOUSES[0]); /* initial */ }, []); // eslint-disable-line

  return (
    <div className="space-y-5" data-testid="v2-mutasi-page">
      <h2 className="text-2xl font-semibold flex items-center gap-2"><BarChart3 className="w-6 h-6" />Laporan Mutasi</h2>

      <Card className="p-5 rounded-2xl">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Lokasi</Label>
            <Select value={loc} onValueChange={setLoc}>
              <SelectTrigger className="mt-1" data-testid="v2-mutasi-loc"><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_LOC.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Dari Tanggal</Label>
            <Input type="date" className="mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="v2-mutasi-from" />
          </div>
          <div>
            <Label className="text-xs">Sampai Tanggal</Label>
            <Input type="date" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="v2-mutasi-to" />
          </div>
          <Button onClick={() => run()} disabled={loading} data-testid="v2-mutasi-run">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Tampilkan
          </Button>
        </div>
        <div className="mt-3 flex justify-end">
          <Button variant="outline" size="sm" disabled={!data} data-testid="v2-mutasi-export"
            onClick={() => {
              const qs = new URLSearchParams({ location: loc });
              if (from) qs.set("date_from", from);
              if (to) qs.set("date_to", to);
              downloadFile(api, `/api/v2/reports/mutasi/export?${qs.toString()}`, `mutasi_${loc}.xlsx`);
            }}>
            <FileDown className="w-4 h-4 mr-1" />Export Excel
          </Button>
        </div>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Saldo Awal" value={data.totals.saldo_awal} />
            <Stat label="Masuk" value={`+${data.totals.masuk}`} accent="text-[hsl(var(--badge-success))]" />
            <Stat label="Keluar" value={`-${data.totals.keluar}`} accent="text-destructive" />
            <Stat label="Saldo Akhir" value={data.totals.saldo_akhir} accent="text-primary" />
          </div>

          {(Object.keys(data.breakdown?.masuk || {}).length > 0 || Object.keys(data.breakdown?.keluar || {}).length > 0) && (
            <Card className="p-5 rounded-2xl">
              <h3 className="font-semibold mb-3 text-sm">Rincian per Jenis Mutasi</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.breakdown.masuk || {}).map(([k, v]) => (
                  <Badge key={`in-${k}`} variant="success">{k}: +{v}</Badge>
                ))}
                {Object.entries(data.breakdown.keluar || {}).map(([k, v]) => (
                  <Badge key={`out-${k}`} variant="danger">{k}: -{v}</Badge>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-5 rounded-2xl">
            <TableScroll>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Merchandize</TableHead><TableHead>Club</TableHead><TableHead>Size</TableHead>
                  <TableHead className="text-right">Saldo Awal</TableHead>
                  <TableHead className="text-right">Masuk</TableHead>
                  <TableHead className="text-right">Keluar</TableHead>
                  <TableHead className="text-right">Saldo Akhir</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">Tidak ada mutasi pada periode ini.</TableCell></TableRow>}
                  {data.rows.map((r, i) => (
                    <TableRow key={i} data-testid="v2-mutasi-row">
                      <TableCell className="text-sm">{r.product_name || "-"}</TableCell>
                      <TableCell>{r.club}</TableCell>
                      <TableCell>{r.size}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.saldo_awal}</TableCell>
                      <TableCell className="text-right tabular-nums text-[hsl(var(--badge-success))]">+{r.masuk}</TableCell>
                      <TableCell className="text-right tabular-nums text-destructive">-{r.keluar}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{r.saldo_akhir}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableScroll>
          </Card>
        </>
      )}
    </div>
  );
}
